import { XIcon } from "@heroicons/react/outline";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";

const IntroSection = ({
  icon,
  title,
  body,
}: {
  icon: TopicflowIconType;
  title: string;
  body: React.ReactNode;
}) => (
  <div className="flex items-start gap-4">
    <div className="shrink-0 w-10 h-10 rounded-[10px] bg-blue-50 flex items-center justify-center">
      <TopicflowIcon type={icon} className="w-5 h-5 text-blue-500" />
    </div>
    <div className="flex flex-col gap-0.5">
      <div className="tf-header-base-dark">{title}</div>
      <div className="tf-body-sm-light">{body}</div>
    </div>
  </div>
);

const ProfileIntroDialog = ({ onClose }: { onClose: () => void }) => {
  const label = useLabel();

  return (
    <Modal onClose={onClose}>
      <div className="p-12 flex flex-col gap-10">
        <div className="flex items-start justify-between gap-10">
          <div className="flex flex-col gap-1">
            <h2 className="tf-header-2xl">Welcome to the Coaching Space</h2>
            <div className="tf-body-sm-light">
              This space gathers everything you need to coach and collaborate
              with someone in one place.
            </div>
          </div>
          <Button
            icon={XIcon}
            theme={buttonTheme.iconGray}
            size={ButtonSize.large}
            aria-label="Close intro dialog"
            onClick={onClose}
          />
        </div>

        <div className="flex flex-col gap-7">
          <IntroSection
            icon="meeting"
            title={`${label("1-on-1", { capitalize: true, pluralize: true })}`}
            body={`Record meetings, capture topics and notes together in real time, and get an AI summary of each conversation after it ends.`}
          />
          <IntroSection
            icon="lockClosed"
            title="Private Notes"
            body="Save private thoughts, observations, concerns, and wins about this person. Only you can see them."
          />
          <IntroSection
            icon="kpi"
            title="Work History & Insights"
            body="See a rolling summary of their activity, plus AI-generated coaching insights to help you support them as a manager."
          />
          <IntroSection
            icon="dataflow"
            title="Artifacts & Data"
            body={`Track ${label("action item", { pluralize: true })}, ${label(
              "goal",
              { pluralize: true },
            )}, ${label("feedback")}, ${label("recognition")}, ${label(
              "review",
              { pluralize: true },
            )}, and career progression — all linked back to this person.`}
          />
          <IntroSection
            icon="ai"
            title="Aggregated for you and AI"
            body="Everything on this profile is aggregated so you can reference it at a glance, and ask Topicflow AI questions about it anytime."
          />
        </div>

        <div className="mt-4">
          <Button
            className="w-full"
            theme={buttonTheme.primary}
            size={ButtonSize.large}
            onClick={onClose}
            text="Go To Profile"
          />
        </div>
      </div>
    </Modal>
  );
};

export default ProfileIntroDialog;
